import {
  useGetAllStudentsQuery,
  useAddStudentMutation,
  useGetStudentByIdQuery,
  useUpdateStudentMutation,
  useDeleteStudentMutation,
  useUploadStudentPhotoMutation,
  useUploadStudentDobDocumentMutation,
  useMarkStudentEnrolledMutation,
  useUpdateStudentStatusMutation,
  useTransferStudentMutation,
  useGetStudentByIdPublicQuery,
  useUpdateStudentPublicMutation,
  useUploadStudentClassProofDocumentMutation,
} from "../api/studentApi";
import { StudentResponseType } from "../types/studentResponse";
import { StudentPayloadType } from "../types/studentRequest";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { useState } from "react";
import { showToast } from "../../../../core/utils/alert";

export const useStudent = (params?: {
  size?: number;
  search?: string;
  page?: number;
  center_id?: number;
  course_id?: number;
  stage_id?: number;
  session_id?: number;
  batch_id?: number;
  status?: string;
  fee_type?: string;
  gender?: string;
  sort_by?: string;
  sort_order?: string;
  marks?: boolean;
  skip?: boolean;
}) => {
  const { skip, ...queryParams } = params || {};
  const { data, isLoading, isFetching, isError, error } = useGetAllStudentsQuery(
    queryParams,
    { skip }
  );

  const [
    addStudentMutation,
    { isLoading: isAdding, isError: isAddError, error: addError },
  ] = useAddStudentMutation();

  const [
    updateStudentMutation,
    { isLoading: isUpdating, isError: isUpdateError, error: updateError },
  ] = useUpdateStudentMutation();

  const [
    updateStudentMutationPublic,
    { isLoading: isUpdatingPublic, isError: isUpdateErrorPublic, error: updateErrorPublic },
  ] = useUpdateStudentPublicMutation();

  const [
    updateStudentStatusMutation,
    {
      isLoading: isUpdatingStatus,
      isError: isUpdateStatusError,
      error: updateStatusError,
    },
  ] = useUpdateStudentStatusMutation();

  const [
    deleteStudentMutation,
    { isLoading: isDeleting, isError: isDeleteError, error: deleteError },
  ] = useDeleteStudentMutation();

  const [
    uploadStudentPhotoMutation,
    { isLoading: isUploading, error: uploadError },
  ] = useUploadStudentPhotoMutation();

  const [uploadStudentDobDocumentMutation] =
    useUploadStudentDobDocumentMutation();

  const [uploadStudentClassProofDocumentMutation] =
    useUploadStudentClassProofDocumentMutation();

  const [markStudentEnrolledMutation] = useMarkStudentEnrolledMutation();

  const [transferStudentMutation] = useTransferStudentMutation();

  // Status management state
  const [statusUpdating, setStatusUpdating] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showStarterKitModal, setShowStarterKitModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [isDeactivateFromDelete, setIsDeactivateFromDelete] = useState(false);

  const students: StudentResponseType[] = data?.data?.items || [];

  const isLoadingState = isLoading;
  const errorState = error;
  const isErrorState = isError;

  const errorMessage = isErrorState ? getApiErrorMessage(errorState) : null;

  const paginationData = {
    totalPages: data?.data?.totalPages,
    pageSize: data?.data?.pageSize,
    totalItems: data?.data?.totalItems,
    currentPage: data?.data?.currentPage,
  };

  const addNewStudent = async (payload: StudentPayloadType) => {
    try {
      const response = await addStudentMutation(payload).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to add new student:--->", error);
      return {
        success: false,
        error: getApiErrorMessage(error),
      };
    }
  };

  const useGetStudentById = (id: string) => {
    // console.log("id", id);
    const { data } = useGetStudentByIdQuery({ id }, { skip: !id });
    return data?.data.student;
  };

  const useGetStudentByIdPublic = (id: string) => {
    // console.log("id", id);
    const { data } = useGetStudentByIdPublicQuery({ id }, { skip: !id });
    return data?.data.student;
  };

  const updateStudent = async (id: string, payload: StudentPayloadType) => {
    try {
      const response = await updateStudentMutation({ id, payload }).unwrap();
      return { success: true, data: response };
    } catch (error) {
      console.error("Failed to update student:--->", error);
      return {
        success: false,
        error: getApiErrorMessage(error),
      };
    }
  };

  const updateStudentPublic = async (id: string, payload: StudentPayloadType) => {
    try {
      const response = await updateStudentMutationPublic({ id, payload }).unwrap();
      return { success: true, data: response };
    } catch (error) {
      console.error("Failed to update student:--->", error);
      return {
        success: false,
        error: getApiErrorMessage(error),
      };
    }
  };

  const deleteStudent = async (id: string) => {
    try {
      const response = await deleteStudentMutation({ id }).unwrap();
      return { success: true, data: response };
    } catch (error: any) {
      if (error.status === 409) {
        return {
          success: false,
          error: "Associated records exist",
          status: 409,
        };
      }
      return {
        success: false,
        error: getApiErrorMessage(error) || "Failed to delete student",
      };
    }
  };

  const updateStudentStatus = async (id: string, status: string) => {
    try {
      const response = await updateStudentStatusMutation({
        id,
        status,
      }).unwrap();
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: isUpdateStatusError
          ? getApiErrorMessage(updateStatusError)
          : "Failed to update student status",
      };
    }
  };

  const deactivateStudent = async (id: string) => {
    return updateStudentStatus(id, "inactive");
  };

  const activateStudent = async (id: string) => {
    return updateStudentStatus(id, "active");
  };

  // Enhanced status management functions
  const handleActivate = async (studentId: number | string) => {
    const id = studentId.toString();
    setSelectedStudentId(id);
    setShowActivateModal(true);
  };

  const handleDeactivate = async (studentId: number | string) => {
    const id = studentId.toString();
    setStatusUpdating((prev) => ({ ...prev, [id]: true }));

    try {
      const result = await deactivateStudent(id);
      if (result.success) {
        showToast({
          title: "Success",
          text: "Student deactivated successfully",
          icon: "success",
        });
      } else {
        showToast({
          title: "Error",
          text: result.error || "Failed to deactivate student",
          icon: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Error",
        text: "An unexpected error occurred",
        icon: "error",
      });
    } finally {
      setStatusUpdating((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedStudentId(id.toString());
    setShowDeleteModal(true);
    setIsDeactivateFromDelete(true);
  };

  const handleDeactivateClick = (id: string | number) => {
    setSelectedStudentId(id.toString());
    setShowDeactivateModal(true);
  };

  const handleStarterKitClick = (id: string | number) => {
    setSelectedStudentId(id.toString());
    setShowStarterKitModal(true);
  };

  const handleTransferClick = (id: string | number) => {
    setSelectedStudentId(id.toString());
    setShowTransferModal(true);
  };

  const transferStudent = async (payload: { student_id: number; to_center_id: number; remarks?: string }) => {
    try {
      const response = await transferStudentMutation(payload).unwrap();
      return { success: true, data: response };
    } catch (error) {
      console.error("Failed to transfer student:--->", error);
      return {
        success: false,
        error: getApiErrorMessage(error) || "Failed to transfer student",
      };
    }
  };

  const uploadStudentPhoto = async (id: string, file: File) => {
    try {
      const response = await uploadStudentPhotoMutation({ id, file }).unwrap();

      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to upload student photo:--->", error);
      return {
        success: false,
        error: uploadError
          ? getApiErrorMessage(uploadError)
          : "Failed to upload photo",
      };
    }
  };

  const uploadStudentDobDocument = async (id: string, file: File, currentStatus?: string) => {
    try {
      const response = await uploadStudentDobDocumentMutation({
        id,
        file,
      }).unwrap();

      // If upload is successful and student is pending, enroll them automatically
      if (response.success && currentStatus === "pending") {
        await markStudentEnrolledMutation({ id }).unwrap();
      }

      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to upload student DOB document:--->", error);
      return {
        success: false,
        error: getApiErrorMessage(error) || "Failed to upload DOB document",
      };
    }
  };

  const uploadStudentClassProofDocument = async (id: string, file: File) => {
    try {
      const response = await uploadStudentClassProofDocumentMutation({
        id,
        file,
      }).unwrap();

      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to upload student class proof document:--->", error);
      return {
        success: false,
        error: getApiErrorMessage(error) || "Failed to upload class proof document",
      };
    }
  };

  const getStudentState = () => ({
    students,
    isLoading: isLoadingState,
    isFetching,
    error: errorMessage,
  });

  // Status management state getters and setters
  const getStatusManagementState = () => ({
    statusUpdating,
    selectedStudentId,
    showDeleteModal,
    showDeactivateModal,
    showActivateModal,
    isDeactivateFromDelete,
  });

  return {
    getStudentState,
    useGetStudentById,
    useGetStudentByIdPublic,
    addNewStudent,
    updateStudent,
    updateStudentPublic,
    deleteStudent,
    deactivateStudent,
    activateStudent,
    updateStudentStatus,
    transferStudent,
    uploadStudentPhoto,
    uploadStudentDobDocument,
    uploadStudentClassProofDocument,
    isAdding,
    isUpdating,
    isUpdatingPublic,
    isUpdatingStatus,
    isDeleting,
    isUploading,
    addError: isAddError ? getApiErrorMessage(addError) : null,
    updateError: isUpdateError ? getApiErrorMessage(updateError) : null,
    updateErrorPublic: isUpdateErrorPublic ? getApiErrorMessage(updateErrorPublic) : null,
    updateStatusError: isUpdateStatusError
      ? getApiErrorMessage(updateStatusError)
      : null,
    deleteError: isDeleteError ? getApiErrorMessage(deleteError) : null,
    uploadError: uploadError ? getApiErrorMessage(uploadError) : null,
    paginationData,
    // Enhanced status management functions
    handleActivate,
    handleDeactivate,
    handleDeleteClick,
    handleDeactivateClick,
    handleStarterKitClick,
    handleTransferClick,
    // Status management state
    statusUpdating,
    setStatusUpdating,
    selectedStudentId,
    setSelectedStudentId,
    showDeleteModal,
    setShowDeleteModal,
    showDeactivateModal,
    setShowDeactivateModal,
    showActivateModal,
    setShowActivateModal,
    showStarterKitModal,
    setShowStarterKitModal,
    showTransferModal,
    setShowTransferModal,
    isDeactivateFromDelete,
    setIsDeactivateFromDelete,
    getStatusManagementState,
  };
};
